/**
 * Utility module: data/parseData.js.
 */

import content from '../../../locales/en/content.json' with { type: 'json' };
import latinize from '../latinize/index.js';
import localStringPairs from './stringReplacements.js';

/** Normalize locale `story.rephrases` ({from,to} objects) → [from,to] pairs. */
const rephrasesToPairs = (rephrases) =>
  rephrases
    .filter((r) => r && typeof r.from === 'string' && r.from.length > 0)
    .map(({ from, to }) => [from, to ?? '']);

/**
 * Serialize a string with prettier's default quote rules so the
 * auto-generated `stringReplacements.js` doesn't churn through the
 * pre-commit formatter.
 */
const formatStringLiteral = (s) => {
  const hasSingle = s.includes("'");
  const hasDouble = s.includes('"');
  if (hasSingle && !hasDouble) return JSON.stringify(s);
  const inner = JSON.stringify(s)
    .slice(1, -1)
    .replace(/\\"/g, '"')
    .replace(/'/g, "\\'");
  return `'${inner}'`;
};

/** Render the full `stringReplacements.js` source for a given set of pairs. */
const buildStringReplacementsFile = (pairs) => {
  const body = pairs
    .map(
      ([from, to]) =>
        `  [${formatStringLiteral(from)}, ${formatStringLiteral(to)}],`
    )
    .join('\n');

  return `/**
 * Array of string replacements.
 * Auto-synced from \`locales/en/content.json\` → \`story.rephrases\`.
 * Used as a fallback when the locale content is unavailable.
 * @type {Array<Array<string>>}
 */
export default [
${body}
];
`;
};

// Once-per-process guard so HMR doesn't loop on repeated writes.
let syncAttempted = false;

/**
 * Mirror the latest rephrases back to `stringReplacements.js` so the
 * local fallback stays in sync. Node-only, once per process, skips no-op
 * writes to avoid HMR reload loops.
 */
const syncLocalStringReplacements = async (pairs) => {
  if (syncAttempted) return;
  if (typeof window !== 'undefined') return;
  syncAttempted = true;

  try {
    const [{ readFile, writeFile }, { fileURLToPath }, path] =
      await Promise.all([
        import('node:fs/promises'),
        import('node:url'),
        import('node:path'),
      ]);

    const here = path.dirname(fileURLToPath(import.meta.url));
    const target = path.join(here, 'stringReplacements.js');

    const nextContents = buildStringReplacementsFile(pairs);

    // Skip the write if the file is already up to date — this is what
    // prevents Vite / SvelteKit from picking up a "change" and reloading
    // in a loop.
    let currentContents = '';
    try {
      currentContents = await readFile(target, 'utf8');
    } catch {
      /* file missing — fall through and write */
    }

    if (currentContents === nextContents) return;
    await writeFile(target, nextContents, 'utf8');
  } catch {
    /* best-effort sync; ignore errors */
  }
};

/**
 * Apply string-replacement rephrases (from `locales/en/content.json` →
 * `story.rephrases`, falling back to the bundled `stringReplacements.js`)
 * to every string in the given JSON object.
 *
 * @param {Object} json
 * @returns {Object}
 */
export default (json) => {
  const localeRephrases =
    Array.isArray(content?.story?.rephrases) ? content.story.rephrases : [];

  const localePairs = rephrasesToPairs(localeRephrases);

  const usingLocale = localePairs.length > 0;
  const stringPairs = usingLocale ? localePairs : localStringPairs;

  let modifiedJson = JSON.stringify(json);

  // Transliterate any extended Latin / diacritic characters in the data
  // (e.g. team or player names like `Curaçao`, `Türkiye`, `Łódź`) to plain
  // ASCII so downstream consumers get a consistent, lookup-friendly form.
  // Operating on the stringified JSON is safe because `latinize` only
  // touches Latin-script glyphs and leaves JSON syntax characters intact.
  modifiedJson = latinize(modifiedJson);

  stringPairs.forEach(([stringToReplace, replacementString]) => {
    if (!stringToReplace) return;
    modifiedJson = modifiedJson.replace(
      new RegExp(stringToReplace, 'g'),
      replacementString
    );
  });

  const parsedJson = JSON.parse(modifiedJson);

  if (usingLocale) syncLocalStringReplacements(localePairs);

  return parsedJson;
};
